import { Typography } from '@material-ui/core';
import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import { isUndefined } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { Context as NavigationContext } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import useFeature from '@swf/core/hooks/useFeature';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import useModal from '@swf/core/hooks/useModal';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { TitleProp } from '@swf/core/types/Props';

import trPromptVideo from '../../../../assets/video/tr-prompt-video.mp4';
import trScreenVideo from '../../../../assets/video/tr-screen-video.mp4';
import trVideoMobile from '../../../../assets/video/tr-video-mobile.mp4';
import { CashierFlowType } from '../../../../types/Cashier';
import { executeHistoryPush } from '../../../../utils/cashier';
import Prompt from '../../../Prompt/Prompt';
import TransitionalExitPrompt from '../../../TransitionalExitPrompt/TransitionalExitPrompt';

import {
  TXTCLI_Resume,
  TXTMOB_Are_you_sure_you_want_to_exitX,
  TXTWCL_Are_you_sureX,
  TXTWCL_Back_to_deposit_1,
  TXTWCL_Confirm_Exit,
  TXTWCL_Exit_This_Page,
  TXTWCL_Leave_anyway,
  TXTWCL_Stay_On_This_Page,
  TXTWCL_You_may_lose_your_progress_if__ELL,
  TXTWCL_Your_account_is_not_verified_y_ELL,
  TXTWCL_Your_deposit_will_be_cancelled_ELL
} from './Header.messages.json';
import HeaderComponent from './components/HeaderComponent/HeaderComponent';

export interface Buttons {
  hideCloseButton?: boolean;
  back?: boolean;
  onBackClick?: () => void;
  onCloseClick?: React.MouseEventHandler;
  allowMultiClick?: boolean;
  exitWizardOnBackClick?: boolean;
}
interface Props extends TitleProp {
  buttons?: Buttons;
  modal?: boolean;
  showExitPrompt?: boolean;
  withImage?: boolean;
  showSGImage?: boolean;
  showNavigationHeader?: boolean;
  hideProgressBar?: boolean;
}

export interface HeaderPropsProp {
  headerProps: Props;
}

const Header: React.FunctionComponent<Props> = ({
  buttons,
  modal = false,
  showExitPrompt = true,
  ...rest
}) => {
  const exitWizard = useWizardExit();
  const { goPrevious, previous } = useContext(NavigationContext);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isUK = useSelector(select.cms.getSiteId) === Sites.UK;
  const isDE = useSelector(select.cms.getSiteId) === Sites.DE;
  const isPoker = useSelector(select.cms.getBrandId) === BrandTypes.POKER_STARS;
  const isMobile = useSelector(select.device.isMobile);
  const isSwcSignupRmfEcopokerLobby = useFeature(FeatureFlag.SwcSignupRmfEcopokerLobby);
  const isSwcSignupRmfEcoEransitionTwo = useFeature(FeatureFlag.SwcSignupRmfEcoEransitionTwo);
  const fromEvent = useFromEvent();
  const { onModalClose, triggerModalClose, isPrompt } = useModal();
  const [backButtonDisabled, setBackButtonDisabled] = useState(false);
  const [exitFlowConfirmation, setExitFlowConfirmation] = useState(false);
  const [ecoTransition, setEcoTransition] = useState(false);
  const [handler, setHandler] = useState(false);
  const { pathname } = useLocation();
  const isRmf = pathname.includes('rmf');
  const { auth } = useDispatch<Dispatch>();

  const {
    data: { cashierFlow, signupPassed, rmfPassed }
  } = useWizardContext();
  const isDepositFlow = cashierFlow === CashierFlowType.FirstDeposit;
  const shouldUpdatePromptMessage = isRmf && (isDE || isUK);

  let promptTitle;
  let promptConfirmExitButton;
  let promptDismissExitButton;
  if (isDepositFlow) {
    promptTitle = TXTWCL_Are_you_sureX;
    promptConfirmExitButton = TXTWCL_Leave_anyway;
    promptDismissExitButton = TXTWCL_Back_to_deposit_1;
  } else if (shouldUpdatePromptMessage) {
    promptTitle = TXTWCL_Are_you_sureX;
    promptConfirmExitButton = TXTWCL_Confirm_Exit;
    promptDismissExitButton = TXTCLI_Resume;
  } else {
    promptTitle = TXTWCL_Confirm_Exit;
    promptConfirmExitButton = TXTWCL_Exit_This_Page;
    promptDismissExitButton = TXTWCL_Stay_On_This_Page;
  }

  const handleRegulatoryExitFlow = async () => {
    exitWizard();
    if (signupPassed && !rmfPassed) {
      auth.logout();
    }
  };

  const handleExitRmfFlow = useCallback(() => {
    if (isSwcSignupRmfEcopokerLobby && signupPassed) {
      return setEcoTransition(true);
    }
    exitWizard();
  }, [exitWizard, isSwcSignupRmfEcopokerLobby, signupPassed]);

  const handleBackClick = useCallback(() => {
    setBackButtonDisabled(!buttons?.allowMultiClick);
    if (buttons?.exitWizardOnBackClick) {
      exitWizard();
      return;
    }
    if (buttons?.onBackClick) {
      buttons.onBackClick();
      executeHistoryPush(isMobileEmbedded);
    } else {
      goPrevious();
    }
  }, [buttons, exitWizard, goPrevious, isMobileEmbedded]);

  const handleExitPrompt = useCallback(() => {
    showExitPrompt ? setExitFlowConfirmation(true) : handleExitRmfFlow();
  }, [handleExitRmfFlow, showExitPrompt]);

  const handleCloseClick = useCallback(
    (event?: React.MouseEvent) => {
      if (event && buttons?.onCloseClick) {
        buttons.onCloseClick(event);
        return;
      }
      handleExitPrompt();
    },
    [buttons, handleExitPrompt]
  );

  useEffect(() => {
    const listener = () => {
      if (onModalClose && isPrompt) {
        triggerModalClose();
      }
      if (onModalClose && !isPrompt) {
        triggerModalClose();
        executeHistoryPush(isMobileEmbedded);
      } else if (!backButtonDisabled && (buttons?.back || (!buttons && previous))) {
        handleBackClick();
      } else {
        executeHistoryPush(isMobileEmbedded);
        handleCloseClick();
      }
    };

    const timer = setTimeout(() => window.addEventListener('popstate', listener), 0);

    return () => {
      setTimeout(() => window.removeEventListener('popstate', listener), 0);
      clearTimeout(timer);
    };
  }, [
    backButtonDisabled,
    buttons,
    handleBackClick,
    handleCloseClick,
    isMobileEmbedded,
    isPrompt,
    onModalClose,
    previous,
    triggerModalClose
  ]);

  useEffect(() => {
    if (isMobileEmbedded) {
      const displayBackButton = isUndefined(buttons?.back)
        ? (!buttons && previous) || modal
        : buttons?.back;
      window.starsweb?.app.setBackButton?.(displayBackButton);
      if (!handler) {
        fromEvent(document, 'starsbeforeclose').subscribe((e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          setHandler(true);
        });
      }
    }
  }, [buttons, fromEvent, handler, isMobileEmbedded, modal, previous]);

  useEffect(() => {
    if (handler) {
      if (onModalClose && isPrompt) {
        triggerModalClose();
        handleCloseClick();
      } else if (onModalClose && !isPrompt) {
        triggerModalClose();
      } else {
        handleCloseClick();
      }
      setHandler(false);
    }
  }, [handleCloseClick, handler, onModalClose, triggerModalClose, isPrompt]);

  useEffect(() => {
    executeHistoryPush(isMobileEmbedded);
    if (isMobileEmbedded && window.history.length <= 1) {
      executeHistoryPush(isMobileEmbedded);
    }
  }, [isMobileEmbedded]);

  return (
    <>
      <Helmet>
        {isSwcSignupRmfEcopokerLobby && (
          <link rel="preload" href={trPromptVideo} type="video/mp4" as="video" />
        )}
        {isSwcSignupRmfEcoEransitionTwo && (
          <link
            rel="preload"
            href={isMobile ? trVideoMobile : trScreenVideo}
            type="video/mp4"
            as="video"
          />
        )}
      </Helmet>
      <HeaderComponent
        buttons={buttons}
        backButtonDisabled={backButtonDisabled}
        modal={modal}
        handleExitPrompt={handleExitPrompt}
        handleCloseClick={handleCloseClick}
        handleBackClick={handleBackClick}
        {...rest}
      />
      {exitFlowConfirmation && (
        <Prompt
          variant="info"
          title={{
            translation: promptTitle
          }}
          primaryButton={{
            translation: promptDismissExitButton
          }}
          secondaryButton={{
            translation: promptConfirmExitButton
          }}
          onPrimaryButtonClick={() => setExitFlowConfirmation(false)}
          onSecondaryButtonClick={
            (isUK || isDE) && isRmf ? handleRegulatoryExitFlow : handleExitRmfFlow
          }
          onClose={() => setExitFlowConfirmation(false)}
          isExitPrompt
        >
          <Typography>
            {isDepositFlow ? (
              <Translation {...TXTWCL_Your_deposit_will_be_cancelled_ELL} />
            ) : shouldUpdatePromptMessage ? (
              <Translation {...TXTWCL_Your_account_is_not_verified_y_ELL} />
            ) : (
              <>
                <Translation {...TXTWCL_You_may_lose_your_progress_if__ELL} />
                <br />
                <Translation {...TXTMOB_Are_you_sure_you_want_to_exitX} />
              </>
            )}
          </Typography>
        </Prompt>
      )}
      {ecoTransition && signupPassed && isPoker && <TransitionalExitPrompt />}
    </>
  );
};

export default Header;
